import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/Select/Select";
import { Switch } from "../../components/Switch/Switch";
import { Label } from "../../components/Label/Label";
import { Input } from "../../components/Input/Input";
import "../Schedule/NewNiwari.css";
import "./Setting.css";
import { Height } from "@mui/icons-material";

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("general");
  const [language, setLanguage] = useState(i18n.language || "ja");
  const [darkMode, setDarkMode] = useState(false);
  const [maxTime, setMaxTime] = useState("0.01");
  const [maxIteration, setMaxIteration] = useState("32");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [fontFamily, setFontFamily] = useState(
    'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
  );
  const [fontSize, setFontSize] = useState("24");
  const [fontWeight, setFontWeight] = useState("400");
  const [saved, setSaved] = useState(false);
  const [collapseSidebar, setCollapseSidebar] = useState(false);
  const [collapseFilters, setCollapseFilters] = useState(false);
  const [logCrash, setLogCrash] = useState(false);
  const [apiResponseCodes, setApiResponseCodes] = useState("200,400");
  const [repeatedPattern, setRepeatedPattern] = useState("color");
  const [workingShiftColor, setWorkingShiftColor] = useState("#34d399");
  const [nonWorkingShiftColor, setNonWorkingShiftColor] = useState("#d1d5db");
  const [displayRole, setDisplayRole] = useState("roleName");
  const [displayShift, setDisplayShift] = useState("shiftCode");
  const [density, setDensity] = useState("compact");

  useEffect(() => {
    setLanguage(i18n.language);
  }, [i18n.language]);

  const handleLanguageChange = (value) => {
    setLanguage(value);
    i18n.changeLanguage(value);
  };

  const handleFontFamilyChange = (value) => setFontFamily(value);
  const handleFontSizeChange = (value) => setFontSize(String(value));

  const saveSettings = () => {
    const payload = {
      language,
      darkMode,
      maxTime,
      maxIteration,
      metaTitle,
      metaDescription,
      fontFamily,
      fontSize,
      fontWeight,
      collapseSidebar,
      collapseFilters,
      logCrash,
      apiResponseCodes,
      repeatedPattern,
      workingShiftColor,
      nonWorkingShiftColor,
      displayRole,
      displayShift,
      density,
    };
    try {
      localStorage.setItem("app_settings", JSON.stringify(payload));
      // notify other parts of the app that settings changed
      try {
        window.dispatchEvent(
          new CustomEvent("appSettingsUpdated", { detail: payload }),
        );
      } catch (e) {}
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      // fallback: alert
      // eslint-disable-next-line no-alert
      alert("Unable to save settings");
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem("app_settings");
      if (raw) {
        const s = JSON.parse(raw);
        if (s.language) setLanguage(s.language);
        if (typeof s.darkMode === "boolean") setDarkMode(s.darkMode);
        if (s.maxTime) setMaxTime(s.maxTime);
        if (s.maxIteration) setMaxIteration(s.maxIteration);
        if (s.metaTitle) setMetaTitle(s.metaTitle);
        if (s.metaDescription) setMetaDescription(s.metaDescription);
        if (s.fontFamily) setFontFamily(s.fontFamily);
        if (s.fontSize) setFontSize(String(s.fontSize));
        if (s.fontWeight) setFontWeight(String(s.fontWeight));
        if (typeof s.collapseSidebar === "boolean")
          setCollapseSidebar(s.collapseSidebar);
        if (typeof s.collapseFilters === "boolean")
          setCollapseFilters(s.collapseFilters);
        if (typeof s.logCrash === "boolean") setLogCrash(s.logCrash);
        if (s.apiResponseCodes) setApiResponseCodes(s.apiResponseCodes);
        if (s.repeatedPattern) setRepeatedPattern(s.repeatedPattern);
        if (s.workingShiftColor) setWorkingShiftColor(s.workingShiftColor);
        if (s.nonWorkingShiftColor)
          setNonWorkingShiftColor(s.nonWorkingShiftColor);
        if (s.displayRole) setDisplayRole(s.displayRole);
        if (s.displayShift) setDisplayShift(s.displayShift);
        if (s.density) setDensity(s.density);
      }
    } catch (e) {
      // ignore
      // console.warn('Failed to load settings', e);
    }
  }, []);

  return (
    <main className="w-full nn-card">
      <div
        className="nn-page page-schedule-layout"
        style={{
          fontFamily: fontFamily,
          fontSize: `${fontSize}px`,
          position: "relative",
        }}
      >
        <aside className="w-56 border-r border-gray-200 py-4 px-4">
          <ul className="space-y-1 text-sm text-gray-600">
            {["general", "language", "optimize", "meta"].map((key) => (
              <li key={key}>
                <button
                  onClick={() => setActiveTab(key)}
                  className={`nn-btn nn-btn--ghost nn-btn--sm nn-tab ${activeTab === key ? "nn-tab--active" : ""}`}
                  aria-pressed={activeTab === key}
                >
                  {key === "general" && t("settings.general")}
                  {key === "language" && t("settings.language")}
                  {key === "optimize" && t("settings.optimize")}
                  {key === "meta" && t("settings.meta")}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="w-full nn-card">
          {activeTab === "general" && (
            <section className="space-y-6">
              <h3 className="nn-step-header">{t("settings.general")}</h3>
              <div className="flex items-center justify-between">
                <Label className="text-sm text-gray-700">
                  {t("settings.darkMode")}
                </Label>
                <Switch checked={darkMode} onCheckedChange={setDarkMode} />
              </div>
              <div className="flex items-end gap-4">
                <div style={{ minWidth: 260 }}>
                  <Label className="text-sm text-gray-700 mb-1 block">
                    Font family
                  </Label>
                  <Select
                    value={fontFamily}
                    onValueChange={handleFontFamilyChange}
                  >
                    <SelectTrigger className="w-full border border-gray-300 rounded-md px-2 py-1 bg-white hover:border-blue-500 focus:ring-1 focus:ring-blue-400 text-sm transition">
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent className="rounded-md shadow-md border border-gray-200">
                      <SelectItem
                        value={
                          'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial'
                        }
                        className="px-4 py-2 hover:bg-gray-100 text-sm"
                      >
                        Inter / System
                      </SelectItem>
                      <SelectItem
                        value={"Arial, Helvetica, sans-serif"}
                        className="px-4 py-2 hover:bg-gray-100 text-sm"
                      >
                        Arial
                      </SelectItem>
                      <SelectItem
                        value={
                          '"Noto Sans JP", "Hiragino Kaku Gothic ProN", Meiryo, sans-serif'
                        }
                        className="px-4 py-2 hover:bg-gray-100 text-sm"
                      >
                        Noto Sans JP
                      </SelectItem>
                      <SelectItem
                        value={'"Times New Roman", Times, serif'}
                        className="px-4 py-2 hover:bg-gray-100 text-sm"
                      >
                        Times New Roman
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ width: 120 }}>
                  <Label className="text-sm text-gray-700 mb-1 block">
                    Font size (px)
                  </Label>
                  <Input
                    type="number"
                    min="12"
                    max="24"
                    value={fontSize}
                    onChange={(e) => handleFontSizeChange(e.target.value)}
                    className="w-20 text-sm px-2 py-1"
                    style={{ height: "32px" }}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <Label className="text-sm text-gray-700 mb-1 block">
                    Font weight
                  </Label>
                  <select
                    value={fontWeight}
                    onChange={(e) => setFontWeight(e.target.value)}
                    className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  >
                    <option value="300">300</option>
                    <option value="400">400</option>
                    <option value="500">500</option>
                    <option value="600">600</option>
                    <option value="700">700</option>
                  </select>
                </div>
              </div>
            </section>
          )}

          {activeTab === "language" && (
            <section className="space-y-6">
              <h3 className="nn-step-header">{t("settings.language")}</h3>
              <div>
                <Label className="text-sm text-gray-700 mb-1 block">
                  {t("settings.language")}
                </Label>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white hover:border-blue-500 focus:ring-1 focus:ring-blue-400 text-sm transition">
                    <SelectValue placeholder={t("settings.selectLanguage")} />
                  </SelectTrigger>
                  <SelectContent className="rounded-md shadow-md border border-gray-200">
                    <SelectItem
                      value="en"
                      className="px-4 py-2 hover:bg-gray-100 text-sm"
                    >
                      {t("settings.english")}
                    </SelectItem>
                    <SelectItem
                      value="ja"
                      className="px-4 py-2 hover:bg-gray-100 text-sm"
                    >
                      {t("settings.japanese")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </section>
          )}

          {activeTab === "optimize" && (
            <section className="space-y-6">
              <h3 className="nn-step-header">{t("settings.optimize")}</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-700 mb-1 block">
                    {t("settings.maxTime")}
                  </Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={maxTime}
                    onChange={(e) => setMaxTime(e.target.value)}
                    className="w-40 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-700 mb-1 block">
                    {t("settings.maxIteration")}
                  </Label>
                  <Input
                    type="number"
                    value={maxIteration}
                    onChange={(e) => setMaxIteration(e.target.value)}
                    className="w-40 text-sm"
                  />
                </div>
              </div>
            </section>
          )}

          {activeTab === "meta" && (
            <section className="space-y-6">
              <h3 className="nn-step-header">{t("settings.meta")}</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-700 mb-1 block">
                    {t("settings.metaTitle")}
                  </Label>
                  <Input
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-700 mb-1 block">
                    {t("settings.metaDescription")}
                  </Label>
                  <textarea
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none h-24"
                  />
                </div>
              </div>
            </section>
          )}
        </main>
        <div className="settings-save-wrapper">
          <button className="settings-save-btn" onClick={saveSettings}>
            <span className="settings-save-icon">✓</span>
            <span className="settings-save-label">Save</span>
          </button>
          {saved && <span className="settings-saved-indicator">Saved</span>}
        </div>
      </div>
    </main>
  );
}
